
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import ProtectedRoute from "./components/ProtectedRoute";
import CookieConsent from "./components/common/CookieConsent";
import Homepage from "./pages/Homepage";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import CreateNewPassword from "./pages/CreateNewPassword";
import Dashboard from "./pages/Dashboard";
import People from "./pages/People";
import Memories from "./pages/Memories";
import MemoryDetail from "./pages/MemoryDetail";
import Suggestions from "./pages/Suggestions";
import SuggestionDetail from "./pages/SuggestionDetail";
import ContactDetail from "./pages/ContactDetail";
import AddContact from "./pages/AddContact";
import AddInteraction from "./pages/AddInteraction";
import NotFound from "./pages/NotFound";
import Profile from "./pages/Profile";
import Onboarding from "./pages/Onboarding";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import BrandPlaybook from "./pages/BrandPlaybook";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import ComingSoon from "./pages/ComingSoon";
import ReferralPage from "./pages/ReferralPage";
import ReferralTerms from "./pages/ReferralTerms";
import ProductionGuard from "./components/ProductionGuard";
import ScrollToTop from "./components/ScrollToTop";
import { queryClient } from "./lib/react-query";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <CookieConsentProvider>
          <AuthProvider>
            <Routes>
            {/* Public pages - always available */}
            <Route path="/" element={<Homepage />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/referral-terms" element={<ReferralTerms />} />
            <Route path="/coming-soon" element={<ComingSoon />} />
            <Route path="/ref/:referralCode" element={<ReferralPage />} />
            
            {/* Auth pages - public access */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/create-new-password" element={<CreateNewPassword />} />

            {/* Protected pages - require authentication */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/people" element={<ProtectedRoute><People /></ProtectedRoute>} />
            <Route path="/memories" element={<ProtectedRoute><Memories /></ProtectedRoute>} />
            <Route path="/memories/:id" element={<ProtectedRoute><MemoryDetail /></ProtectedRoute>} />
            <Route path="/suggestions" element={<ProtectedRoute><Suggestions /></ProtectedRoute>} />
            <Route path="/suggestions/:id" element={<ProtectedRoute><SuggestionDetail /></ProtectedRoute>} />
            <Route path="/contacts/:id" element={<ProtectedRoute><ContactDetail /></ProtectedRoute>} />
            <Route path="/contacts/:id/add-interaction" element={<ProtectedRoute><AddInteraction /></ProtectedRoute>} />
            <Route path="/add-contact" element={<ProtectedRoute><AddContact /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            
            {/* Internal/Admin pages - blocked in production */}
            <Route path="/index" element={<ProductionGuard><Index /></ProductionGuard>} />
            <Route path="/brand-playbook" element={<ProductionGuard><BrandPlaybook /></ProductionGuard>} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
          <CookieConsent />
        </AuthProvider>
      </CookieConsentProvider>
    </BrowserRouter>
  </TooltipProvider>
  {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
</QueryClientProvider>
);

export default App;
